import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { isPdf, isVideo } from './MediaStrip';
import BRAND from '../brandConfig';
import Wordmark from './Wordmark';
import DisplayOptions from './DisplayOptions';
import CardMenu, { MenuIcons } from './CardMenu';
import { t } from '../i18n';
import { fmtDate, fmtDateTime, fmtDay, fmtMoney } from '../lib/format';
import { useDisplayOptions } from '../lib/displayOptions';

// A work order or an estimate as a sheet of paper.
//
// Julian asked for "View & Print PDF", and the honest answer on this stack is
// the browser's own print dialog: every phone and desktop it runs on can
// already turn a printed page into a PDF (iOS: Share → Print → Save to Files;
// Chrome: Destination → Save as PDF). A server-side PDF library would mean a
// second renderer to keep in step with this one, a bigger deploy, and a file
// that still has to be downloaded before anyone can look at it.
//
// So this is one component that is BOTH the on-screen document and the printed
// one. What you see is what comes out — no separate template that quietly
// drifts from the screen.
//
// The print rules live in index.css under `@media print`: everything on the
// page is hidden and only `.print-doc` is made visible again. That is the
// reliable way to print one thing out of a single-page app where every screen
// is mounted at once.

const Row = ({ label, children }) => children ? (
  <div className="doc-row"><span className="doc-row-l">{label}</span><span className="doc-row-v">{children}</span></div>
) : null;

// What can actually be printed as a picture. Videos were never in a work
// order's photo list; PDFs can be, now that a customer can attach one.
const shotsOf = (it) => (it.photos || []).filter(u => !isPdf(u) && !isVideo(u));

export default function PrintDoc({
  request, invoice, kind, onClose,
  onDeleteWorkOrder, onCreateWorkOrder, onDuplicate, onDelete,
}) {
  const display = useDisplayOptions();
  const [optionsOpen, setOptionsOpen] = useState(false);

  // The pay link as a QR code, so a printed or emailed invoice can be paid from
  // a phone camera. Drawn here, in the browser, so the link never goes to a
  // third-party QR service.
  const payUrl = kind === 'invoice' ? invoice?.payUrl : null;
  const [qr, setQr] = useState(null);
  useEffect(() => {
    if (!payUrl) { setQr(null); return; }
    let live = true;
    QRCode.toDataURL(payUrl, { margin: 1, width: 220, color: { dark: '#000000', light: '#ffffff' } })
      .then(url => { if (live) setQr(url); })
      .catch(() => {});
    return () => { live = false; };
  }, [payUrl]);

  // ── The invoice ────────────────────────────────────────────────────────
  // A bill is not the estimate with a different word at the top: it is
  // letterhead, who it is billed to, the terms, and what is owed. So it is its
  // own sheet rather than another branch through the one above — but it prints
  // through exactly the same mechanism (`.print-doc`, the browser's own Save
  // as PDF), so there is still no second renderer to keep in step.
  if (kind === 'invoice') {
    if (!invoice) return null;
    const co = BRAND.company || {};
    const c = invoice.client || {};
    // Bill To means the billing address when there is one — an owner who lives
    // out of state, a rental billed to the landlord — and the service address
    // when there isn't, which is the same rule the client record stores.
    const billLines = (c.billingAddress
      ? [c.billingAddress, c.billingAddress2, [c.billingCity, c.billingState, c.billingZip].filter(Boolean).join(', ')]
      : [c.address, c.address2, [c.city, c.state, c.zip].filter(Boolean).join(', ')]
    ).filter(Boolean);

    const isMoney = (l) => ['discount', 'tax'].includes((l.trade || '').trim().toLowerCase());
    const items = (invoice.lines || []).filter(l => !isMoney(l));
    const adjustments = (invoice.lines || []).filter(isMoney);
    const subtotal = items.reduce((t, l) => t + (Number(l.amount) || 0), 0);
    const money = (n) => (n < 0 ? `−${fmtMoney(Math.abs(n))}` : fmtMoney(n));

    return (
      <>
        <div className="doc-bar print-hide">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>‹ Close</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }} className="doc-bar-mid">
            Invoice · {invoice.number || '—'}
          </span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={() => window.print()}>Print / Save PDF</button>
            {/* Joist's More. Everything here acts on this one document. */}
            <CardMenu
              label="More"
              items={[
                { key: 'disp', label: t('disp.open', 'Display options'), icon: MenuIcons.eye, onSelect: () => setOptionsOpen(true) },
                { key: 'dup', label: 'Duplicate', icon: MenuIcons.copy, hidden: !onDuplicate, onSelect: () => onDuplicate(invoice) },
                { key: 'print', label: 'View & print PDF', icon: MenuIcons.printer, onSelect: () => window.print() },
                { key: 'del', label: 'Delete invoice', icon: MenuIcons.trash, danger: true, hidden: !onDelete, onSelect: () => onDelete(invoice) },
              ]}
            />
          </span>
        </div>
        <DisplayOptions open={optionsOpen} onClose={() => setOptionsOpen(false)} />

        <div className="doc-scroll print-hide-scroll">
          <div className="print-doc">
            <div className="doc-inv-word">Invoice</div>

            <header className="doc-inv-head">
              <div className="doc-inv-from">
                <Wordmark size={30} />
                <div className="doc-brand-name" style={{ marginTop: 8 }}>{co.legalName || BRAND.name.full}</div>
                {co.phone && <div>Phone: {co.phone}</div>}
                {co.billingEmail && <div>Email: {co.billingEmail}</div>}
              </div>

              <div className="doc-inv-to">
                <div className="doc-strong">Bill To</div>
                <div>{c.name}</div>
                {billLines.map((l, i) => <div key={i}>{l}</div>)}
                {c.phone && <div>{c.phone}</div>}

                <div className="doc-inv-meta">
                  <Row label="Payment terms">{co.paymentTerms || 'Due upon receipt'}</Row>
                  <Row label="Invoice #">{invoice.number || '—'}</Row>
                  <Row label="Date">{invoice.date ? fmtDay(invoice.date) : ''}</Row>
                </div>
              </div>
            </header>

            <div className="doc-inv-cols">
              <span>Description</span>
              <span>Total</span>
            </div>

            {/* The same three switches the estimate reads (Display Options):
                a fixed price for "replace the gate" is a number they agreed to,
                and printing "3 hrs × $85" under it invites a conversation about
                the hourly rate rather than about the gate. */}
            {items.map((l, i) => (
              <div key={l._id || i} className="doc-inv-line">
                <div>
                  <div className="doc-inv-line-t">{l.trade}</div>
                  {l.notes && <div className="doc-inv-line-d">{l.notes}</div>}
                  {((display.qty && l.qty != null) || (display.rate && l.rate != null)) && (
                    <div className="doc-inv-line-w">
                      {display.qty && l.qty != null && <span>{l.qty}</span>}
                      {display.qty && l.qty != null && display.rate && l.rate != null && <span> × </span>}
                      {display.rate && l.rate != null && <span>{fmtMoney(l.rate)}</span>}
                    </div>
                  )}
                </div>
                {display.itemTotals && <div className="doc-inv-line-a">{money(Number(l.amount) || 0)}</div>}
              </div>
            ))}

            <div className="doc-inv-sums">
              <div className="doc-inv-sum"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
              {adjustments.map((l, i) => (
                <div className="doc-inv-sum" key={l._id || `a${i}`}>
                  <span>{l.trade}</span><span>{money(Number(l.amount) || 0)}</span>
                </div>
              ))}
              <div className="doc-inv-sum doc-inv-sum-t"><span>Total</span><span>{fmtMoney(invoice.total)}</span></div>
            </div>

            {invoice.project?.title && (
              <div className="doc-inv-job">Work: {invoice.project.title}</div>
            )}

            {payUrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 22, padding: '14px 16px', border: '1px solid #cbd5e1', borderRadius: 8 }}>
                {qr && <img src={qr} alt="QR code to pay this invoice online" style={{ width: 96, height: 96, flex: '0 0 auto' }} />}
                <div style={{ minWidth: 0 }}>
                  <div className="doc-strong">Pay online</div>
                  <div style={{ fontSize: 12.5, marginTop: 4 }}>Scan the code, or open this link. Bank transfer or card.</div>
                  <div style={{ fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
                    <a href={payUrl}>{payUrl.replace(/^https?:\/\//, '')}</a>
                  </div>
                </div>
              </div>
            )}

            <div className="doc-foot" style={{ marginTop: 26 }}>
              {co.legalName || BRAND.name.full} · {BRAND.serviceArea.regionLabel} · {BRAND.company.website}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!request) return null;
  const isWorkOrder = kind === 'work-order';
  const wo = request.workOrder || {};
  const est = request.estimate || {};
  const items = request.items?.length
    ? request.items
    : [{ title: request.serviceType, description: request.description, photos: request.photos }];

  const title = isWorkOrder ? 'Work Order' : 'Estimate';
  // An estimate has no number of its own, so it borrows the work order's when
  // there is one and falls back to the tail of the job's id — which is at least
  // unique, and is what support would ask for anyway.
  const number = isWorkOrder
    ? (wo.number || '—')
    : (wo.number ? wo.number.replace('WO-', 'EST-') : `EST-${String(request._id).slice(-6).toUpperCase()}`);
  const dated = isWorkOrder ? (wo.createdAt || request.updatedAt) : (est.submittedAt || request.createdAt);
  const when = wo.scheduledFor || request.appointmentAt;

  return (
    <>
      {/* The toolbar is on screen only — `print-hide` takes it off the paper. */}
      <div className="doc-bar print-hide">
        <button className="btn btn-ghost btn-sm" onClick={onClose}>‹ Close</button>
        <span style={{ fontSize: 12, color: 'var(--muted)' }} className="doc-bar-mid">{title} · {number}</span>
        <span style={{ display: 'flex', gap: 8 }}>
          {/* Reading the sheet is when you notice it is the wrong one — so the
              way to take it back is here too, not only on the card behind. */}
          {isWorkOrder && onDeleteWorkOrder && wo.number && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
              onClick={() => onDeleteWorkOrder(request)}>Delete</button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => window.print()}>Print / Save PDF</button>
          {/* Joist's More, on the estimate. Display options was a button of its
              own before; it lives in here now with the rest. */}
          {!isWorkOrder && (
            <CardMenu
              label="More"
              items={[
                {
                  key: 'wo',
                  label: request.workOrder?.number ? `Work order ${request.workOrder.number}` : 'Create work order',
                  icon: MenuIcons.doc,
                  hidden: !onCreateWorkOrder,
                  onSelect: () => onCreateWorkOrder(request),
                },
                { key: 'disp', label: t('disp.open', 'Display options'), icon: MenuIcons.eye, onSelect: () => setOptionsOpen(true) },
                { key: 'dup', label: 'Duplicate', icon: MenuIcons.copy, hidden: !onDuplicate, onSelect: () => onDuplicate(request) },
                { key: 'print', label: 'View & print PDF', icon: MenuIcons.printer, onSelect: () => window.print() },
                { key: 'del', label: 'Delete estimate', icon: MenuIcons.trash, danger: true, hidden: !onDelete, onSelect: () => onDelete(request) },
              ]}
            />
          )}
        </span>
      </div>
      <DisplayOptions open={optionsOpen} onClose={() => setOptionsOpen(false)} />

      <div className="doc-scroll print-hide-scroll">
        <div className="print-doc">
          <header className="doc-head">
            <div className="doc-brand">
              <Wordmark size={26} />
              <div>
                <div className="doc-brand-name">{BRAND.name.full}</div>
                <div className="doc-brand-sub">{BRAND.location} · {BRAND.slogan}</div>
              </div>
            </div>
            <div className="doc-meta">
              <div className="doc-title">{title}</div>
              <div className="doc-number">{number}</div>
              <div className="doc-date">{dated ? fmtDate(dated) : ''}</div>
            </div>
          </header>

          <section className="doc-cols">
            <div>
              <div className="doc-label">Customer</div>
              <div className="doc-strong">{request.homeownerId?.name || request.fullName}</div>
              {request.phone && <div>{request.phone}</div>}
              {request.email && !request.email.includes('@lead.local') && !request.email.startsWith('no-email+') && <div>{request.email}</div>}
            </div>
            <div>
              <div className="doc-label">Service address</div>
              <div className="doc-strong">{request.serviceAddress}</div>
              {(request.city || request.zip) && <div>{[request.city, request.zip].filter(Boolean).join(' ')}</div>}
            </div>
          </section>

          <section className="doc-block">
            <Row label={isWorkOrder ? 'Scheduled' : 'Requested'}>
              {when ? fmtDateTime(when) : (request.bestTime ? `${request.bestTime} window — day to be confirmed` : 'To be confirmed')}
            </Row>
            <Row label="Urgency">{request.urgency}</Row>
            {isWorkOrder && <Row label="Assigned to">{request.assignedVendorId?.businessName || 'Not yet assigned'}</Row>}
            {!isWorkOrder && <Row label="Status">{request.estimateStatus === 'approved' ? `Approved ${est.submittedAt ? fmtDate(est.submittedAt) : ''}` : 'Awaiting approval'}</Row>}
          </section>

          {/* The jobs, in the customer's own words — the same list everywhere
              else in the app reads, so the paper cannot say something the
              screen doesn't. */}
          <section>
            <div className="doc-label">{isWorkOrder ? 'Scope of work' : 'What was asked for'}</div>
            {items.map((it, i) => (
              <div key={i} className="doc-item">
                <div className="doc-item-head">
                  <span className="doc-item-n">{i + 1}</span>
                  <span className="doc-item-t">{it.title || it.serviceType}</span>
                  {it.serviceType && it.serviceType !== it.title && <span className="doc-item-trade">{it.serviceType}</span>}
                </div>
                {it.description && it.description !== it.title && <div className="doc-item-d">{it.description}</div>}
                {/* Pictures only. A customer's inspection PDF is attached to
                    the same line, and an <img> pointed at it prints as a broken
                    icon on the sheet the crew carries — the file is opened from
                    the job in the app, not from the paper. */}
                {isWorkOrder && shotsOf(it).length > 0 && (
                  <div className="doc-shots">
                    {shotsOf(it).slice(0, 4).map((u, n) => <img key={n} src={u} alt="" />)}
                  </div>
                )}
                {/* A box to tick on the van's copy. The crew works down this
                    list, and a job with no line to sign off is a job that gets
                    remembered differently by two people. */}
                {isWorkOrder && <div className="doc-item-done">Done <span className="doc-box"></span> Notes ______________________________</div>}
              </div>
            ))}
          </section>

          {isWorkOrder && wo.instructions && (
            <section className="doc-block">
              <div className="doc-label">Site instructions</div>
              <div className="doc-note">{wo.instructions}</div>
            </section>
          )}

          {/* Money — on the ESTIMATE only.
              A work order carries no price at all, by Julian's rule: it is the
              instruction to do the work, it goes to a technician and a tenant
              as well as to the customer, and a number on it is a number being
              discussed with people who are not party to it. The price lives on
              the estimate, which is the document that was agreed, and on the
              invoice, which is the one that gets paid.

              Which of the workings appear is the reader's choice — see
              lib/displayOptions.js. The overall total is not negotiable: a
              priced document with no price on it is not a document. */}
          {!isWorkOrder && est.breakdown?.length > 0 && (
            <section className="doc-block">
              <div className="doc-label">Estimate</div>
              {est.breakdown.map((r, i) => (
                <div key={i}>
                  <div className="doc-line">
                    <span>{r.label}</span>
                    <span className="doc-line-nums">
                      {display.qty && r.qty != null && <span className="doc-num">{r.qty}</span>}
                      {display.rate && r.rate != null && <span className="doc-num">{fmtMoney(r.rate)}</span>}
                      {display.itemTotals && <span className="doc-num doc-num-t">{fmtMoney(r.cost)}</span>}
                    </span>
                  </div>
                  {/* What the price buys, written on the line itself. This is
                      the half of an estimate a customer actually reads: a
                      number is something to argue with, a scope is something
                      to agree to. It prints. */}
                  {r.description && <div className="doc-line-desc">{r.description}</div>}
                </div>
              ))}
              <div className="doc-total"><span>Total</span><span>{fmtMoney(est.amount)}</span></div>
              {est.notes && (
                <><div className="doc-label" style={{ marginTop: 12 }}>Notes</div><div className="doc-note">{est.notes}</div></>
              )}
            </section>
          )}

          <section className="doc-sign">
            {isWorkOrder ? (
              <>
                <div><div className="doc-sign-line"></div>Work completed by</div>
                <div><div className="doc-sign-line"></div>Customer signature / date</div>
              </>
            ) : (
              <>
                <div><div className="doc-sign-line"></div>Accepted by</div>
                <div><div className="doc-sign-line"></div>Date</div>
              </>
            )}
          </section>

          <footer className="doc-foot">
            {BRAND.name.full} · {BRAND.serviceArea.regionLabel} · {BRAND.company.website}
            {!isWorkOrder && <><br />This estimate is based on the work described above. Anything found once work starts is quoted separately before it is done.</>}
          </footer>
        </div>
      </div>
    </>
  );
}
